/**
 * Where a top too long for its slab is cut (2026-10-20, the user).
 *
 * A seam goes on a joint between two base cabinets, where a cabinet wall
 * holds it up; never over the dishwasher, whose steam and vibration open a
 * seam; and at least 6" from a sink or cooktop cutout, the weak spot in the
 * stone. Those are the fabrication guides' rules (Slabwise, Cambria). Some
 * shops hide a seam AT the sink instead; the user chose to keep clear of it.
 *
 * None of it may cost a seam: a piece is still cut into the fewest parts its
 * slab allows, and each cut goes where the rules allow within the window
 * that count leaves — the farthest such joint, else the farthest clear spot,
 * else the slab's length as before. The farthest keeps each part as long as
 * the old "a full slab and the rest" made it, so the slab count holds.
 *
 * `cutStationsOf` reads what stands under a top when the take-off makes its
 * pieces (`slabPieces.topPieces`); the packers cut by `lengthCuts`. Stations
 * are kept as positions along the top's own axis in plan metres, measured
 * from the plan origin, so a corner re-cut (`countertopCorners.js`), which
 * moves a top's ends along that axis, leaves them valid. Pure.
 */
const { footprintFor } = require('../../../geometry/objectFootprint');
const {
  doPolygonsOverlap,
} = require('../../../bathroom-analyzer/pipeline/geometry');
const { isSink } = require('../../../sink-types');
const {
  isLowerCabinetLike,
} = require('../../../group-customization/anchorTypes');

const IN_M = 0.0254;
const EPS = 1e-6;
// How far from a sink or cooktop cutout a seam must stay.
const CUTOUT_CLEARANCE_M = 6 * IN_M;
// What stands under a top overlaps its plan by at least this.
const UNDER_M = 0.01;

/** One full length plus the rest, along one side (spec D4 / D5). */
const cutsAlong = (size, max) => {
  const out = [];
  let left = size;
  while (left > max + EPS) {
    out.push(max);
    left -= max;
  }
  out.push(left);
  return out;
};

const axisOf = (ry) => ({ x: Math.cos(ry), z: -Math.sin(ry) });
const along = (p, u) => p.x * u.x + p.z * u.z;

const isDishwasher = (o) => o?.type === 'dishwasher' || o?.category?.dishwasher;
const isCooktop = (o) => o?.type === 'cooktop' || o?.category?.cooktop;
const isBaseCabinet = (o) =>
  isLowerCabinetLike(o) && !o?.category?.['upper-cabinet'];

/**
 * What stands under `top`, as stations along its own axis (plan metres from
 * the origin): `joints`, the ends of the base cabinets beneath it, and
 * `keepOut`, the spans a seam may not fall inside — a dishwasher's width, a
 * sink's or cooktop's widened by 6" each side.
 */
function cutStationsOf(top, objects) {
  const ry = top?.rotation?.y || 0;
  const topPlan = footprintFor(
    top?.center,
    top?.dimensions?.x,
    top?.dimensions?.z,
    ry
  );
  const out = { joints: [], keepOut: [] };
  if (!topPlan) return out;
  const u = axisOf(ry);
  for (const o of objects || []) {
    if (o === top || !o?.dimensions) continue;
    const isCutout = isSink(o) || isCooktop(o);
    if (!isCutout && !isDishwasher(o) && !isBaseCabinet(o)) continue;
    const plan = footprintFor(
      o.center,
      o.dimensions.x,
      o.dimensions.z,
      o.rotation?.y
    );
    if (!plan || !doPolygonsOverlap(topPlan, plan, UNDER_M)) continue;
    const at = plan.map((p) => along(p, u));
    const a = Math.min(...at);
    const b = Math.max(...at);
    if (isCutout) {
      out.keepOut.push([a - CUTOUT_CLEARANCE_M, b + CUTOUT_CLEARANCE_M]);
    } else if (isDishwasher(o)) out.keepOut.push([a, b]);
    else out.joints.push(a, b);
  }
  return out;
}

/**
 * A piece's stations in inches from its low end along its run (the end its
 * first part is cut from), or null when it has none to cut by.
 */
function stationsFromLo(piece) {
  const { frame, cutStations } = piece || {};
  if (!frame || !cutStations) return null;
  const lo = along(frame, axisOf(frame.ry)) - frame.lengthM / 2;
  const toIn = (m) => (m - lo) / IN_M;
  const joints = [
    ...new Set(cutStations.joints.map((m) => +toIn(m).toFixed(4))),
  ]
    .filter((j) => j > EPS && j < frame.lengthM / IN_M - EPS)
    .sort((x, y) => x - y);
  const keepOut = cutStations.keepOut
    .map(([a, b]) => [toIn(a), toIn(b)])
    .sort((x, y) => x[0] - y[0]);
  return { joints, keepOut };
}

// Strictly inside a kept-out span: a span's edge is a cabinet wall.
const keptOut = (x, keepOut) =>
  keepOut.some(([a, b]) => x > a + EPS && x < b - EPS);

// The farthest point at or below `hi`, and not below `lo`, outside every
// kept-out span, or null.
const farthestClear = (lo, hi, keepOut) => {
  let x = hi;
  for (let guard = 0; guard <= keepOut.length; guard++) {
    const span = keepOut.find(([a, b]) => x > a + EPS && x < b - EPS);
    if (!span) return x >= lo - EPS ? x : null;
    x = span[0];
  }
  return null;
};

/**
 * The parts, in inches from the low end, a piece `lengthIn` long is cut into
 * on stock `maxIn` long: as few as the stock allows, each cut placed by the
 * rules above. With no stations it is `cutsAlong`, a full length and the
 * rest.
 */
function lengthCuts(lengthIn, maxIn, stations) {
  const plain = cutsAlong(lengthIn, maxIn);
  if (!stations || plain.length < 2) return plain;
  const parts = plain.length;
  const cuts = [];
  let pos = 0;
  for (let k = 1; k < parts; k++) {
    // the cut must leave what is left for the parts still to come
    const lo = Math.max(pos + EPS, lengthIn - (parts - k) * maxIn);
    const hi = Math.min(pos + maxIn, lengthIn - EPS);
    const joint = [...stations.joints]
      .reverse()
      .find(
        (j) => j >= lo - EPS && j <= hi + EPS && !keptOut(j, stations.keepOut)
      );
    const cut = joint ?? farthestClear(lo, hi, stations.keepOut) ?? hi;
    cuts.push(Math.min(Math.max(cut, lo), hi));
    pos = cuts[cuts.length - 1];
  }
  const ends = [0, ...cuts, lengthIn];
  return ends.slice(1).map((end, i) => +(end - ends[i]).toFixed(6));
}

module.exports = { cutsAlong, cutStationsOf, stationsFromLo, lengthCuts };
